<template>
  <div class="block block-mode-loading-oneui">
    <div class="block-header border-bottom">
      <h3 class="block-title">
        My Targets
        <small
          v-if="data"
          class="text-muted"
        >{{ data.date }} ({{ data.timezone }})</small>
      </h3>
      <div class="block-options d-flex align-items-center">
        <div
          v-if="loading && data"
          class="spinner-border spinner-border-sm text-primary me-2"
          role="status"
        >
          <span class="visually-hidden">Loading...</span>
        </div>
        <input
          v-model="selectedDate"
          type="date"
          class="form-control form-control-sm"
          style="width: auto;"
          :max="today"
          :disabled="loading"
          :title="data ? `Date in ${data.timezone}` : ''"
          @change="onDateChange"
        >
        <button
          v-if="selectedDate && selectedDate !== today"
          type="button"
          class="btn btn-sm btn-alt-secondary ms-1"
          :disabled="loading"
          @click="resetDate"
        >
          Today
        </button>
        <a
          v-if="reportUrl"
          :href="reportUrl"
          class="btn btn-sm btn-alt-secondary ms-2"
        >
          Published report
          <i class="fa fa-arrow-right ms-1" />
        </a>
      </div>
    </div>
    <div class="block-content block-content-full">
      <div
        v-if="loading && !data"
        class="text-center py-4"
      >
        <div
          class="spinner-grow text-primary"
          role="status"
        >
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <div
        v-else-if="error"
        class="alert alert-danger"
      >
        {{ error }}
      </div>

      <div
        v-else-if="data"
        class="position-relative"
      >
        <div
          class="row"
          :style="{ opacity: loading ? 0.4 : 1, transition: 'opacity 0.2s ease' }"
        >
          <div
            v-for="metric in metrics"
            :key="metric.key"
            class="col-sm-6 col-lg mb-3"
          >
            <div class="fw-semibold text-muted mb-1">
              {{ metric.label }}
            </div>
            <div class="d-flex align-items-end mb-2">
              <span class="fs-2 fw-bold">{{ format(metric.key, count(metric.key)) }}</span>
              <span
                v-if="target(metric.key) !== null"
                class="fs-5 text-muted ms-1"
              > / {{ target(metric.key) }}</span>
            </div>

            <template v-if="target(metric.key) !== null">
              <div
                class="progress"
                style="height: 10px;"
              >
                <div
                  class="progress-bar"
                  :class="reached(metric.key) ? 'bg-success' : 'bg-primary'"
                  role="progressbar"
                  :style="{ width: percent(metric.key) + '%' }"
                  :aria-valuenow="count(metric.key)"
                  aria-valuemin="0"
                  :aria-valuemax="target(metric.key)"
                />
              </div>
              <div
                class="mt-1 fs-sm"
                :class="reached(metric.key) ? 'text-success fw-semibold' : 'text-muted'"
              >
                <template v-if="reached(metric.key)">
                  <i class="fa fa-check-circle" /> Goal reached
                </template>
                <template v-else>
                  {{ format(metric.key, remaining(metric.key)) }} more {{ metric.period }}
                </template>
              </div>
            </template>
            <div
              v-else
              class="fs-sm text-muted"
            >
              {{ metric.period }}
            </div>
          </div>
        </div>
        <div
          v-if="loading && data"
          class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
        >
          <div
            class="spinner-grow text-primary"
            role="status"
          >
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { STAFF_TARGET_METRICS, formatMetric } from './staffTargetMetrics'

export default {
  name: 'StaffTargetsCard',

  props: {
    reportUrl: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      metrics: STAFF_TARGET_METRICS,
      selectedDate: ''
    }
  },

  computed: {
    ...mapGetters('dashboard', {
      data: 'staffTargets',
      loading: 'staffTargetsLoading',
      error: 'staffTargetsError'
    }),

    // Today in the business timezone, as reported by the server.
    today() {
      return this.data ? this.data.today : ''
    }
  },

  watch: {
    // Show the server's date in the picker until the user picks one.
    data(value) {
      if (value && !this.selectedDate) {
        this.selectedDate = value.date
      }
    }
  },

  mounted() {
    this.fetchData()
  },

  methods: {
    ...mapActions('dashboard', ['fetchStaffTargets']),

    async fetchData() {
      try {
        await this.fetchStaffTargets({ date: this.selectedDate || null })
      } catch (error) {
        console.error('Failed to fetch staff targets:', error)
      }
    },

    onDateChange() {
      this.fetchData()
    },

    resetDate() {
      this.selectedDate = this.today
      this.fetchData()
    },

    count(key) {
      return this.data.me[key] || 0
    },

    target(key) {
      const target = this.data.targets[key]
      return target === undefined ? null : target
    },

    reached(key) {
      return this.count(key) >= this.target(key)
    },

    percent(key) {
      const target = this.target(key)
      if (!target) {
        return 0
      }
      return Math.min(100, Math.round((this.count(key) / target) * 100))
    },

    remaining(key) {
      return Math.max(0, this.target(key) - this.count(key))
    },

    format(key, value) {
      return formatMetric(key, value)
    }
  }
}
</script>
